// src/utils/geolocation.ts
import { logger } from '../logger';

export interface GeolocationInfo {
  ip: string;
  country: string;
  city: string;
  region: string;
  timezone: string;
  isp: string;
}

/**
 * Get IP geolocation information
 * Note: This is a demo implementation. In production, this should be done on the backend
 * to avoid exposing API keys and to get the real server IP address.
 */
export const getGeolocationInfo = async (): Promise<GeolocationInfo | null> => {
  try {
    // Skip IP geolocation in development/localhost to avoid CORS issues
    if (window.location.hostname === 'localhost' || 
        window.location.hostname === '127.0.0.1' ||
        process.env.NODE_ENV === 'development') {
      // Debug logging removed for cleaner console
      return {
        ip: '127.0.0.1',
        country: 'Development',
        city: 'Local',
        region: 'Local',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        isp: 'Local Development',
      };
    }

    // Try multiple IP geolocation services as fallbacks
    const services = [
      'https://ipapi.co/json/',
      'https://ipinfo.io/json',
      'https://api.ipify.org?format=json'
    ];

    for (const serviceUrl of services) {
      try {
        const response = await fetch(serviceUrl, {
          mode: 'cors',
          headers: {
            'Accept': 'application/json',
          },
        });
        
        if (!response.ok) {
          continue; // Try next service
        }
        
        const data = await response.json();
        
        // Handle different response formats
        const ip = data.ip || data.query || 'unknown';
        const country = data.country_name || data.country || 'unknown';
        const city = data.city || 'unknown';
        const region = data.region || data.regionName || 'unknown';
        const timezone = data.timezone || 'unknown';
        const isp = data.org || data.isp || 'unknown';
        
        return {
          ip,
          country,
          city,
          region,
          timezone,
          isp,
        };
      } catch (serviceError) {
        console.warn(`Failed to fetch from ${serviceUrl}:`, serviceError);
        continue; // Try next service
      }
    }
    
    throw new Error('All IP geolocation services failed');
    
  } catch (error) {
    console.warn('All geolocation services failed:', error);
    // Return fallback data instead of null to avoid breaking session creation
    return {
      ip: 'unknown',
      country: 'unknown',
      city: 'unknown',
      region: 'unknown',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      isp: 'unknown',
    };
  }
};

/**
 * Get approximate location using browser geolocation API
 * Note: This requires user permission and may not be accurate
 */
export const getBrowserLocation = (): Promise<{ latitude: number; longitude: number } | null> => {
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve(null);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        console.error('Browser geolocation error:', error);
        resolve(null);
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 300000, // 5 minutes
      }
    );
  });
};

/**
 * Enhanced device info with geolocation
 */
export const getEnhancedDeviceInfo = async () => {
  const { getDeviceInfo } = await import('./deviceDetection');
  const deviceInfo = getDeviceInfo();
  
  // Try to get geolocation info
  const geoInfo = await getGeolocationInfo();
  
  return {
    ...deviceInfo,
    ipAddress: geoInfo?.ip || deviceInfo.ipAddress,
    country: geoInfo?.country || deviceInfo.country,
    city: geoInfo?.city || deviceInfo.city,
    region: geoInfo?.region || 'unknown',
    isp: geoInfo?.isp || 'unknown',
  };
};
